import React, { useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react'

import { SELECT_EVENT_NAME } from './constants'
import { useHeadlessListboxContext, useHeadlessListboxState } from './context'

interface HeadlessListboxOptionProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect' | 'value'> {
  children: React.ReactNode;
  disabled?: boolean;
  onSelect?: (value: string | null) => Promise<boolean | void> | boolean | void;
  value?: string | null;
}

/**
 * If no explicit `value` prop was provided we try to derive the option's
 * value from the element's text content, which is not available until after
 * the first render.
 */
function useValueRef (
  value: string | null | undefined,
  ref: React.RefObject<HTMLDivElement>,
  children: React.ReactNode
) {
  const valueRef = useRef(value === undefined ? '' : value)

  useLayoutEffect(() => {
    const optionValue = value === undefined ? (ref.current?.textContent || '') : value

    valueRef.current = optionValue
  }, [children, value])

  return valueRef
}

export function HeadlessListboxOption ({
  children,
  disabled = false,
  onSelect,
  value,
  ...props
}: HeadlessListboxOptionProps) {
  const id = useId()

  const { registerOption, selectOption } = useHeadlessListboxContext()
  const isSelected = useHeadlessListboxState(({ selectedOption }) => selectedOption === id)

  const ref = useRef<HTMLDivElement>(null)
  const valueRef = useValueRef(value, ref, children)

  useLayoutEffect(() => {
    return registerOption(id)
  }, [])

  useEffect(() => {
    function handleSelect () {
      selectOption(id)
      onSelect?.(valueRef.current)
    }

    ref.current?.addEventListener(SELECT_EVENT_NAME, handleSelect)

    return () => {
      ref.current?.removeEventListener(SELECT_EVENT_NAME, handleSelect)
    }
  }, [])

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()

      selectOption(id)
      onSelect?.(valueRef.current)
    },
    [selectOption]
  )

  const handlePointerMove = useCallback(() => {
    selectOption(id)
  }, [selectOption])

  return (
    <div
      {...props}
      aria-disabled={disabled}
      aria-selected={isSelected}
      data-listbox-option=''
      data-disabled={disabled ? '' : undefined}
      data-selected={isSelected ? '' : undefined}
      id={id}
      ref={ref}
      role='option'
      onClick={disabled
        ? undefined
        : (event) => {
            handleClick(event)
            props.onClick?.(event)
          }}
      onPointerMove={disabled ? undefined : handlePointerMove}
    >
      {children}
    </div>
  )
}

HeadlessListboxOption.displayName = 'HeadlessListbox.Option'
